import { useState, useEffect } from "react";
import { Box, Button, Flex, Text } from "@radix-ui/themes";
import * as Popover from "@radix-ui/react-popover";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { Cross2Icon, PlusIcon } from "@radix-ui/react-icons";
import { toast } from "sonner";

import styles from "./Agents.module.scss";
import {
  useTeamsWithAgents,
  useAddAgentToTeam,
  useRemoveAgentFromTeam,
} from "../../api/useTeams";

interface Agent {
  id: string;
  name: string;
  externalId: string;
  teams?: string[];
}

interface AgentDetailsProps {
  agent: Agent | null;
  onClose: () => void;
}

export function AgentDetails({ agent, onClose }: AgentDetailsProps) {
  const [isAddTeamOpen, setIsAddTeamOpen] = useState(false);
  const {
    data: teamsData,
    isLoading,
    refetch: refetchTeams,
  } = useTeamsWithAgents(true);
  const [selectedTeams, setSelectedTeams] = useState<string[]>(
    agent?.teams || []
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [currentTeamId, setCurrentTeamId] = useState("");
  const [isAddingTeam, setIsAddingTeam] = useState(false);
  const [isRemovingTeam, setIsRemovingTeam] = useState(false);
  const [teamToRemove, setTeamToRemove] = useState("");

  const { mutateAsync: addTeam } = useAddAgentToTeam(
    agent?.externalId || "",
    currentTeamId
  );
  const { mutateAsync: removeTeam } = useRemoveAgentFromTeam(
    agent?.externalId || "",
    teamToRemove
  );

  useEffect(() => {
    if (agent) {
      setSelectedTeams(agent.teams || []);
    }
  }, [agent]);

  useEffect(() => {
    async function addTeamToAgent() {
      if (!isAddingTeam || !currentTeamId || !agent) return;

      try {
        await addTeam({
          teamIds: [currentTeamId],
        });
        await refetchTeams();
        setIsAddTeamOpen(false);
        toast("Team added successfully");
      } catch (error) {
        console.error("Failed to add team:", error);
        try {
          // window.quack?.proactiveTrigger();
        } catch (e) {}
        toast("Failed to add team");
      } finally {
        setIsAddingTeam(false);
      }
    }

    addTeamToAgent();
  }, [currentTeamId, isAddingTeam, agent, addTeam, refetchTeams]);

  useEffect(() => {
    async function removeTeamFromAgent() {
      if (!isRemovingTeam || !teamToRemove || !agent) return;

      try {
        await removeTeam({});
        await refetchTeams();
        toast("Team removed successfully");
        setSelectedTeams(selectedTeams.filter((t) => t !== teamToRemove));
      } catch (error) {
        console.error("Failed to remove team:", error);
        try {
          // window.quack?.proactiveTrigger();
        } catch (e) {}
        toast("Failed to remove team");
      } finally {
        setIsRemovingTeam(false);
        setTeamToRemove("");
      }
    }

    removeTeamFromAgent();
  }, [
    teamToRemove,
    isRemovingTeam,
    agent,
    removeTeam,
    refetchTeams,
    selectedTeams,
  ]);

  if (!agent) return null;

  const teams = Object.keys(teamsData || {}).map((name) => ({
    id: teamsData?.[name]?.id || name,
    name,
  }));

  const availableTeams = teams.filter(
    (team) => !selectedTeams.includes(team.name)
  );

  const handleRemoveTeam = (teamName: string) => {
    const team = teams.find((t) => t.name === teamName);
    if (team) {
      setTeamToRemove(team.id);
      setIsRemovingTeam(true);
    }
  };

  const handleAddTeam = (team: { id: string; name: string }) => {
    if (!selectedTeams.includes(team.name)) {
      setCurrentTeamId(team.id);
      setIsAddingTeam(true);
    }
  };

  return (
    <TooltipPrimitive.Provider delayDuration={200}>
      <div className={styles.modalOverlay} onClick={onClose} />
      <div className={styles.rightModal}>
        <div className={styles.modalHeader}>
          <Flex gap="3" direction="column" width="100%">
            <Flex justify="between" align="center" width="100%">
              <Button
                variant="ghost"
                color="gray"
                size="2"
                className={styles.squareIconButton}
                onClick={onClose}
              >
                <TooltipPrimitive.Root>
                  <TooltipPrimitive.Trigger asChild>
                    <Cross2Icon />
                  </TooltipPrimitive.Trigger>
                  <TooltipPrimitive.Content
                    className={styles.tooltipContent}
                    sideOffset={5}
                  >
                    Close
                    <TooltipPrimitive.Arrow className={styles.tooltipArrow} />
                  </TooltipPrimitive.Content>
                </TooltipPrimitive.Root>
              </Button>
            </Flex>
            <Box>
              <h2>{agent.name}</h2>
              <Text size="2" color="gray">
                Manage team assignments
              </Text>
            </Box>
          </Flex>
        </div>

        <div className={styles.modalContent}>
          <Flex direction="column" gap="3">
            <Popover.Root open={isAddTeamOpen} onOpenChange={setIsAddTeamOpen}>
              <Popover.Trigger asChild>
                <Button
                  variant="outline"
                  color="gray"
                  className={`${styles.actionButton} ${styles.addButton}`}
                >
                  <PlusIcon />
                  Add Team
                </Button>
              </Popover.Trigger>
              <Popover.Portal>
                <Popover.Content
                  className={styles.popoverContent}
                  side="bottom"
                  align="start"
                  sideOffset={5}
                  alignOffset={0}
                >
                  <input
                    type="text"
                    className={styles.commandInput}
                    placeholder="Search teams..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    autoFocus={false}
                  />
                  <div className={styles.commandList}>
                    {availableTeams.length === 0 ? (
                      <div className={styles.commandEmpty}>
                        {searchQuery
                          ? "No matching teams found"
                          : "No available teams"}
                      </div>
                    ) : (
                      <div className={styles.commandGroup}>
                        {availableTeams.map((team) => (
                          <button
                            key={team.id}
                            className={styles.commandItem}
                            onClick={() => handleAddTeam(team)}
                          >
                            <span>{team.name}</span>
                            <PlusIcon className={styles.addIcon} />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </Popover.Content>
              </Popover.Portal>
            </Popover.Root>

            <Text size="2" color="gray">
              {selectedTeams.length}{" "}
              {selectedTeams.length === 1 ? "team" : "teams"} assigned
            </Text>

            {selectedTeams.map((team) => (
              <div key={team} className={styles.listItem}>
                <div className={styles.itemContent}>
                  <span className={styles.itemName}>{team}</span>
                </div>
                <Button
                  variant="ghost"
                  size="1"
                  className={styles.removeButton}
                  onClick={() => handleRemoveTeam(team)}
                >
                  <TooltipPrimitive.Root>
                    <TooltipPrimitive.Trigger asChild>
                      <Cross2Icon />
                    </TooltipPrimitive.Trigger>
                    <TooltipPrimitive.Content
                      className={styles.tooltipContent}
                      sideOffset={5}
                    >
                      Remove team
                      <TooltipPrimitive.Arrow className={styles.tooltipArrow} />
                    </TooltipPrimitive.Content>
                  </TooltipPrimitive.Root>
                </Button>
              </div>
            ))}
          </Flex>
        </div>
      </div>
    </TooltipPrimitive.Provider>
  );
}
